import React, { FC, useMemo, useRef, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { useJourneyApi } from '../journey-api-context';
import {
	BreakdownResult,
	JourneyAttribute,
	JourneyFilterState,
	JourneyMeta,
	JourneySegment,
} from '../../../../models/journey-explorer/journey-explorer';
import { formatNumber } from '../journey-explorer-utils';
import { attributeKey, attributeOptions } from '../journey-segments';
import { RequestLoader, useDebouncedRequest } from '../use-debounced-request';

const LOAD_ERROR = 'The breakdown could not be loaded.';
const MIN_SHARE_FOR_HIGHEST = 0.02;

interface SegmentBreakdownProps {
	meta: JourneyMeta;
	filters: JourneyFilterState;
	segment: JourneySegment;
	onKeep: (attribute: JourneyAttribute, value: string) => void;
}

const SegmentBreakdown: FC<SegmentBreakdownProps> = ({ meta, filters, segment, onKeep }) => {
	const apiService = useJourneyApi();
	const apiRef = useRef(apiService);
	apiRef.current = apiService;

	const options = useMemo(() => attributeOptions(meta), [meta]);
	const [selectedKey, setSelectedKey] = useState(options[0]?.key ?? '');
	const [attempt, setAttempt] = useState(0);
	const option = options.find((item) => item.key === selectedKey) ?? options[0];

	const load = useMemo<RequestLoader<BreakdownResult>>(
		() =>
			option
				? (signal) => apiRef.current.getBreakdown({ filters, segment, attribute: option.attribute }, signal)
				: null,
		[filters, segment, option]
	);
	const { data, loading, error } = useDebouncedRequest(load, LOAD_ERROR, attempt);

	if (!option) {
		return <div className={styles.drawer_message}>No customer attributes are set up to break down by.</div>;
	}

	const maxCustomers = Math.max(1, ...(data?.rows ?? []).map((row) => row.customers));
	const minForHighest = (data?.customers ?? 0) * MIN_SHARE_FOR_HIGHEST;
	const highest = (data?.rows ?? [])
		.filter((row) => row.customers >= minForHighest)
		.reduce<BreakdownResult['rows'][number] | null>(
			(best, row) => (!best || row.reached_pct > best.reached_pct ? row : best),
			null
		);

	return (
		<div className={styles.breakdown}>
			<label className={styles.breakdown_picker}>
				<span>Break down by</span>
				<select
					className={styles.flow_select}
					value={attributeKey(option.attribute)}
					onChange={(event) => setSelectedKey(event.target.value)}
				>
					{options.map((item) => (
						<option key={item.key} value={item.key}>
							{item.label}
						</option>
					))}
				</select>
			</label>
			{error ? (
				<div className={styles.drawer_message}>
					{error}
					<button type="button" className={styles.retry} onClick={() => setAttempt((value) => value + 1)}>
						Retry
					</button>
				</div>
			) : !data ? (
				<div className={styles.drawer_message}>
					<span className={styles.spinner} aria-label="Loading" />
				</div>
			) : data.rows.length === 0 ? (
				<div className={styles.drawer_message}>No customers in this group.</div>
			) : (
				<div className={loading ? styles.loading : undefined}>
					<div className={`${styles.breakdown_row} ${styles.breakdown_head}`}>
						<span>{option.label}</span>
						<span>Customers</span>
						<span>Reached goal</span>
						<span />
					</div>
					{data.rows.map((row) => (
						<div key={row.value} className={styles.breakdown_row}>
							<span className={styles.breakdown_value}>
								<span className={styles.breakdown_label} title={row.value}>
									{row.value}
								</span>
								<span className={styles.breakdown_bar}>
									<span style={{ width: `${(row.customers / maxCustomers) * 100}%` }} />
								</span>
							</span>
							<span>{formatNumber(row.customers)}</span>
							<span className={row.reached_pct > data.reached_pct ? styles.goal_text : undefined}>
								{row.reached_pct.toFixed(0)}%
							</span>
							<button
								type="button"
								className={styles.keep_button}
								title={`Keep only ${option.label.toLowerCase()} ${row.value}`}
								onClick={() => onKeep(option.attribute, row.value)}
							>
								Keep
							</button>
						</div>
					))}
					<div className={styles.breakdown_note}>
						{highest && (
							<>
								Best goal rate: <b>{highest.value}</b> at <b>{highest.reached_pct.toFixed(0)}%</b>.{' '}
							</>
						)}
						Keep adds that value as a filter.
						{data.truncated && ' Showing the 100 largest values.'}
					</div>
				</div>
			)}
		</div>
	);
};

export default SegmentBreakdown;
